import React, { Fragment, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Card,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import { StatTile } from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import { OVERALL_COLORS, apiError, formatDate } from './dopsShared'

const PERIODS = [3, 6, 12, 24, 36]

const CurrencyCard = ({ item }) => {
  const pct = Math.min(100, Math.round((item.achieved / Math.max(item.count, 1)) * 100))
  const tone = item.current ? 'success' : 'warning'
  return (
    <Card
      variant="outlined"
      sx={{ p: 1.5, borderRadius: 2.5, borderLeft: 5, borderLeftColor: `${tone}.main` }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Typography fontWeight={800} noWrap>
          {item.label}
        </Typography>
        <Chip
          size="small"
          color={tone}
          label={item.current ? 'Current' : 'Below target'}
          sx={{ borderRadius: 1 }}
        />
      </Stack>
      <Typography variant="body2" mt={0.5}>
        <strong>{item.achieved}</strong> of {item.count} in the last {item.periodDays} days
      </Typography>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={tone}
        sx={{ mt: 1, height: 6, borderRadius: 3 }}
      />
    </Card>
  )
}

const ProcedureRow = ({ row, canOpenRecords, isMobile }) => {
  const [open, setOpen] = useState(false)
  const hasEntries = row.entries?.length > 0
  return (
    <Fragment>
      <TableRow
        hover={hasEntries}
        onClick={() => hasEntries && setOpen((v) => !v)}
        sx={{ cursor: hasEntries ? 'pointer' : 'default', '& > td': { borderBottom: open ? 0 : undefined } }}
      >
        <TableCell padding="checkbox">
          {hasEntries && (
            <IconButton size="small" aria-label={open ? 'Hide entries' : 'Show entries'}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
        <TableCell align="center">{row.performed}</TableCell>
        <TableCell align="center">{row.onCase}</TableCell>
        {!isMobile && (
          <TableCell>{row.lastPerformedAt ? formatDate(row.lastPerformedAt) : '—'}</TableCell>
        )}
      </TableRow>
      {hasEntries && (
        <TableRow>
          <TableCell colSpan={isMobile ? 4 : 5} sx={{ py: 0, ...(open ? {} : { borderBottom: 0 }) }}>
            <Collapse in={open} unmountOnExit>
              <Stack gap={0.5} py={1} pl={{ xs: 0, sm: 6 }}>
                {row.entries.map((entry) => (
                  <Stack
                    key={`${entry.epcr}-${entry.entryId}-${entry.at}`}
                    direction="row"
                    gap={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Typography variant="body2" sx={{ minWidth: 120 }}>
                      {formatDate(entry.at, 'DD/MM/YYYY HH:mm')}
                    </Typography>
                    {canOpenRecords && entry.epcr ? (
                      <Link component={RouterLink} to={`/patient-report-forms/${entry.epcr}`} variant="body2">
                        ePCR {entry.epcrNumber ?? ''}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        ePCR {entry.epcrNumber ?? ''}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={entry.performed ? 'Performed' : 'On case'}
                      color={entry.performed ? 'primary' : 'default'}
                      sx={{ borderRadius: 1 }}
                    />
                    {entry.attempt && (
                      <Typography variant="caption" color="text.secondary">
                        Attempt {entry.attempt}
                      </Typography>
                    )}
                    {entry.outcome && (
                      <Chip
                        size="small"
                        label={entry.outcome}
                        color={entry.outcome === 'Successful' ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  )
}

// A clinician's procedure logbook: GET /logbook/me or /logbook/user/:id.
// `canOpenRecords` links entries to the record (Governance only - clinicians
// can't open submitted records).
const Logbook = ({ endpoint, canOpenRecords }) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [months, setMonths] = useState(12)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState('')

  useEffect(() => {
    if (!endpoint) return
    setLoading(true)
    setFetchError('')
    API.get(`${endpoint}?months=${months}`)
      .then((res) => {
        if (res.status === 200) setData(res.data)
        else setFetchError(apiError(res, 'Unable to load the logbook'))
      })
      .catch(() => setFetchError('Unable to load the logbook'))
      .finally(() => setLoading(false))
  }, [endpoint, months])

  const procedures = (data?.procedures ?? [])
    .slice()
    .sort((a, b) => b.performed + b.onCase - (a.performed + a.onCase))
  const totalPerformed = procedures.reduce((sum, row) => sum + row.performed, 0)
  const intubation = data?.intubation

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          Counted from ePCRs you were on. &quot;Performed&quot; means you are
          recorded as the operator; &quot;on case&quot; entries have no
          operator recorded.
        </Typography>
        <TextField
          select
          size="small"
          label="Period"
          value={months}
          onChange={(e) => setMonths(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {PERIODS.map((value) => (
            <MenuItem key={value} value={value}>
              Last {value} months
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading && <LinearProgress />}
      {fetchError && <Alert severity="error">{fetchError}</Alert>}

      {!loading && !fetchError && data && (
        <>
          <Box
            display="grid"
            gap={1}
            gridTemplateColumns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
          >
            <StatTile
              label="Records reviewed"
              value={String(data.recordsReviewed ?? 0)}
              caption={`Last ${data.months} months`}
            />
            <StatTile label="Procedures performed" value={String(totalPerformed)} />
            <StatTile
              label="Intubation first pass"
              value={intubation?.firstPassRate === null || intubation?.firstPassRate === undefined ? '—' : `${intubation.firstPassRate}%`}
              tone={
                intubation?.firstPassRate === null || intubation?.firstPassRate === undefined
                  ? undefined
                  : intubation.firstPassRate >= 85
                    ? 'success'
                    : 'warning'
              }
              caption={`${intubation?.firstPassSuccesses ?? 0} of ${intubation?.episodes ?? 0} episodes`}
            />
            <StatTile
              label="Completed DOPS"
              value={String(data.dops?.length ?? 0)}
            />
          </Box>

          {data.currency?.length > 0 && (
            <Box>
              <Typography fontWeight={800} mb={1}>
                Skill currency
              </Typography>
              <Box
                display="grid"
                gap={1}
                gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }}
              >
                {data.currency.map((item) => (
                  <CurrencyCard key={item.procedure} item={item} />
                ))}
              </Box>
            </Box>
          )}

          <Card variant="outlined" sx={{ borderRadius: 2.5, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Procedure</TableCell>
                  <TableCell align="center">Performed</TableCell>
                  <TableCell align="center">On case</TableCell>
                  {!isMobile && <TableCell>Last performed</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {procedures.map((row) => (
                  <ProcedureRow
                    key={row.key}
                    row={row}
                    canOpenRecords={canOpenRecords}
                    isMobile={isMobile}
                  />
                ))}
              </TableBody>
            </Table>
          </Card>

          <Box>
            <Typography fontWeight={800} mb={1}>
              Completed DOPS
            </Typography>
            {data.dops?.length ? (
              <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
                {data.dops.map((dops, index) => (
                  <Stack
                    key={dops._id}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    gap={0.5}
                    px={1.5}
                    py={1}
                    onClick={() => navigate(`/dops/${dops._id}`)}
                    sx={{
                      cursor: 'pointer',
                      borderTop: index ? 1 : 0,
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={700}>
                        {dops.procedureLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Performed {formatDate(dops.performedAt)} · assessed {formatDate(dops.completedAt)}
                      </Typography>
                    </Box>
                    {dops.overall && (
                      <Chip
                        size="small"
                        label={dops.overall}
                        color={OVERALL_COLORS[dops.overall] ?? 'default'}
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                  </Stack>
                ))}
              </Card>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No completed DOPS yet. Request one from a completed
                intervention on an ePCR.
              </Typography>
            )}
          </Box>
        </>
      )}
    </Stack>
  )
}

export default Logbook
